// components/LocaleLink.tsx
"use client";

import Link, {LinkProps} from "next/link";
import * as React from "react";
import {useParams} from "next/navigation";
import {i18n, type Locale} from "@/i18n-config";

function useActiveLocale(): Locale {
  const params = useParams<Record<string, string>>();
  const seg = (params?.locale || params?.lang) as string | undefined;
  return (i18n.locales as readonly string[]).includes(seg || "") ? (seg as Locale) : i18n.defaultLocale;
}

function isExternal(href: string) {
  return /^(https?:)?\/\//i.test(href) || href.startsWith("mailto:") || href.startsWith("tel:");
}

function WithLocale(href: string, locale: Locale) {
  if (!href) return `/${locale}`;
  if (href.startsWith("#")) return href; // hash-only links
  if (isExternal(href)) return href; // external links unchanged

  // split path from ?query/#hash
  const match = href.match(/^[^?#]+/);
  const pathPart = match ? match[0] : href;
  const rest = href.slice(pathPart.length);

  const normalized = pathPart.startsWith("/") ? pathPart : `/${pathPart}`;
  const alreadyPrefixed = i18n.locales.some((l) => normalized === `/${l}` || normalized.startsWith(`/${l}/`));

  const localizedPath = alreadyPrefixed ? normalized : `/${locale}${normalized}`;
  return `${localizedPath}${rest}`;
}

export function WithAutoLocale(href: string) {
  const locale = useActiveLocale();
  return WithLocale(href, locale);
}
export type LocaleLinkProps = LinkProps & React.AnchorHTMLAttributes<HTMLAnchorElement>;

export const LocaleLink = React.forwardRef<HTMLAnchorElement, LocaleLinkProps>(function LocaleLink({href, children, ...rest}, ref) {
  const locale = useActiveLocale();
  const finalHref = typeof href === "string" ? WithLocale(href, locale) : href;
  return (
    <Link ref={ref} href={finalHref} {...rest}>
      {children}
    </Link>
  );
});

export default LocaleLink;
